import moment from 'moment-timezone';
import React from 'react';

// Deep import rather than the `../../hooks` barrel, matching the lazy-loaded
// consumers of this atom (e.g. EventServiceRunsContainer) so we don't pull the
// barrel's many re-exported modules into their chunk.
import { useTimezone } from '../../hooks/useTimezone';
import { Tooltip } from './tooltip';

interface Props {
  children: string;
  // Defaults to the current fleet's timezone.
  timezone?: string;
}

// Full precision on purpose: these views exist to order events that are
// milliseconds apart, so an `h:mm A` reading would answer the wrong question.
// The clock and the abbreviation come from a single `moment.tz` call, so they
// cannot end up describing different timezones.
export const readableTimestamp = (raw: string, timezone: string): string => moment.tz(raw, timezone).format('MMM D, YYYY, h:mm:ss.SSS A z');

// The API serves microsecond precision (e.g. 2024-03-20T15:56:25.169815Z), but
// neither `Date` nor `moment` can represent sub-millisecond values, so `raw` must
// be interpolated untouched -- round-tripping it through either silently drops
// the microseconds.
export const rawTimestampLine = (raw: string): string => `${raw} · epoch ms ${new Date(raw).getTime()}`;

// Two audiences, two lines: whoever just wants to know when something happened
// reads the first, whoever is correlating with logs copies the second.
//
// Returned as an element rather than a string because bootstrap's
// `.tooltip-inner` does not set `white-space`, so a `\n` in a string tooltip
// collapses to a space. The two block children give us real line boxes.
export const timestampTooltip = (raw: string, timezone: string): React.ReactElement => (
  <>
    <div>{readableTimestamp(raw, timezone)}</div>
    <div>{rawTimestampLine(raw)}</div>
  </>
);

// Absolute, millisecond-precision timestamp for internal views that order events
// which can be milliseconds apart. Deliberately separate from `Time`, which is
// customer-facing and coarse on purpose.
export function PreciseTime({ children, timezone }: Props) {
  const tz = useTimezone(timezone);

  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <Tooltip tooltip={timestampTooltip(children, tz)}>
        {/* `dateTime` carries the unmodified API value, keeping the full precision
            machine-readable for assistive tech and tests without a hover. */}
        <time dateTime={children}>{moment.tz(children, tz).format('MM/DD HH:mm:ss.SSS')}</time>
      </Tooltip>
    </span>
  );
}
